import React, { Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MarketingHeader } from '../components/MarketingHeader';
import { Mail, MessageCircle, BookOpen, LifeBuoy, ArrowRight, ArrowUpRight } from 'lucide-react';

const Footer = lazy(() => import('../components/Footer'));

const SUPPORT_EMAIL = 'tickets@restaff-n513b1.p.tawk.email';

const CONTACT_CHANNELS = [
  {
    icon: Mail,
    title: 'Support Ticket',
    desc: 'Send us a message and we\'ll respond within one business day.',
    action: `mailto:${SUPPORT_EMAIL}`,
    label: 'Send an email',
    external: true,
  },
  {
    icon: MessageCircle,
    title: 'Live Chat',
    desc: 'Chat with our team in real time from within the app or directly on this site.',
    action: null,
    label: 'Start a conversation',
    external: false,
  },
  {
    icon: BookOpen,
    title: 'Feature Requests',
    desc: 'Have an idea that would improve ReStaff? Submit it and vote on others.',
    action: 'https://vazeeqc.fillout.com/restaff',
    label: 'Submit a request',
    external: true,
  },
  {
    icon: LifeBuoy,
    title: 'System Status',
    desc: 'Check real-time uptime and incident history for all ReStaff services.',
    action: 'https://stats.uptimerobot.com/uXUOFXeFX5',
    label: 'View status page',
    external: true,
  },
];

export default function Contact() {
  return (
    <div className="min-h-screen bg-slate-50">
      <MarketingHeader />

      <main>
        {/* Hero */}
        <section className="py-20 px-4 bg-white border-b border-slate-100 text-center">
          <div className="max-w-2xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              We're here to help
            </h1>
            <p className="mt-4 text-lg text-slate-600">
              Reach out through any channel below — our team is available Monday–Friday.
            </p>
          </div>
        </section>

        {/* Contact cards */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto grid sm:grid-cols-2 gap-6">
            {CONTACT_CHANNELS.map(({ icon: Icon, title, desc, action, label, external }) => (
              <div key={title} className="bg-white rounded-2xl border border-slate-200 p-7 flex flex-col">
                <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center text-orange-500 mb-4">
                  <Icon size={22} />
                </div>
                <h2 className="font-semibold text-lg text-slate-900 mb-2">{title}</h2>
                <p className="text-sm text-slate-600 leading-relaxed flex-1">{desc}</p>
                {action ? (
                  <a
                    href={action}
                    {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors"
                  >
                    {label}
                    {external ? <ArrowUpRight size={14} /> : <ArrowRight size={14} />}
                  </a>
                ) : (
                  <button
                    onClick={() => window.Tawk_API?.toggle?.()}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors text-left"
                  >
                    {label}
                    <ArrowRight size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Partnership / Affiliate */}
        <section className="py-14 px-4 bg-white border-t border-slate-100">
          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>
                Partner with us
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Know other businesses that could benefit from ReStaff? Join our affiliate program and earn
                a recurring commission for every customer you refer. Free to join, no minimum requirements.
              </p>
              <a
                href="https://getrestaff.goaffpro.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700"
              >
                Join the Affiliate Program <ArrowUpRight size={14} />
              </a>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>
                Join the team
              </h2>
              <p className="text-slate-600 leading-relaxed">
                We're always looking for talented people who care deeply about helping businesses manage
                their workforce better. Check out open roles.
              </p>
              <a
                href="https://vazeeqc.fillout.com/restaff"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700"
              >
                View open positions <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4 bg-orange-500">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
              Not a customer yet?
            </h2>
            <p className="mt-3 text-orange-100">Start your free 30-day trial — no credit card needed.</p>
            <Link to="/register" className="mt-6 inline-block">
              <Button className="bg-white text-orange-600 hover:bg-orange-50 font-semibold px-8">
                Start Free Trial
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  );
}
